/**
 * Notifications page (Figma `10229:59948`).
 *
 * One System Notification row, and one switch per module. The row opens the
 * OS notification page where the shell can (`hibroNotification.openSystemSettings`);
 * web has no such page, so the row is not drawn. Each switch is that module's
 * own master, the boolean keys it hung on `platform.notifications`.
 */
import { systemNotificationSettingsAvailable } from '@hibro/shared';
import {
  SETTINGS_CARD,
  SettingsHint,
  SettingsNavRow,
  SettingsPane,
  SettingsSectionTitle,
  SettingsSwitchRow,
} from '@hibro/ui-client';
import { type ReactNode, useState } from 'react';

import { settingsWindowLogError } from './log';
import type { SettingsConfig } from './setting-row';
import type { SettingsTree, SettingsTreeSection } from './tree';

function moduleIcon(tree: SettingsTree, moduleId: string): string | undefined {
  for (const group of tree.groups) {
    for (const page of group.pages) {
      if (page.moduleId === moduleId && page.icon?.type === 'builtin') {
        return page.icon.id;
      }
    }
  }
  return undefined;
}

function openSystemSettings(): (() => Promise<void>) | undefined {
  const root = globalThis as {
    hibroNotification?: { openSystemSettings?: () => Promise<void> };
    hibroWindow?: { platform?: string };
  };
  const open = root.hibroNotification?.openSystemSettings;
  return systemNotificationSettingsAvailable(open, root.hibroWindow?.platform)
    ? open
    : undefined;
}

export function NotificationsSettings({
  sections,
  tree,
  config,
  label,
  t,
}: {
  sections: readonly SettingsTreeSection[];
  tree: SettingsTree;
  config: SettingsConfig;
  label: (moduleId: string, raw: string) => string;
  t: (key: string) => string;
}): ReactNode {
  const open = openSystemSettings();
  const [opening, setOpening] = useState(false);
  const rows = sections.flatMap((section) => {
    if (section.body.kind !== 'keys') {
      return [];
    }
    return section.body.keys.flatMap((key) => {
      const entry = config.schema.get(key);
      if (!entry || entry.type !== 'boolean') {
        return [];
      }
      return [{ section, entry }];
    });
  });

  const jump = (): void => {
    if (!open || opening) {
      return;
    }
    setOpening(true);
    void open()
      .catch((err: unknown) => {
        settingsWindowLogError('open system notification settings failed', {
          reason: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => setOpening(false));
  };

  return (
    <SettingsPane>
      {open ? (
        <div className={SETTINGS_CARD}>
          <SettingsNavRow
            icon="i-setting-9"
            label={t('settingsWindow.notifications.system')}
            last
            busy={opening}
            onClick={jump}
          />
        </div>
      ) : null}
      {config.status === 'ready' && rows.length === 0 ? null : (
        <div className="flex w-full flex-col">
          <SettingsSectionTitle>
            {t('settingsWindow.notifications.apps')}
          </SettingsSectionTitle>
          {config.status === 'ready' ? (
            <div className={SETTINGS_CARD}>
              {rows.map(({ section, entry }, index) => (
                <SettingsSwitchRow
                  key={entry.key}
                  icon={moduleIcon(tree, section.moduleId)}
                  label={
                    entry.title
                      ? label(entry.moduleId, entry.title)
                      : (section.title ?? entry.key)
                  }
                  checked={config.values[entry.key] === true}
                  last={index === rows.length - 1}
                  onChange={(next) => config.write(entry.key, next)}
                />
              ))}
            </div>
          ) : config.status === 'loading' ? null : (
            <SettingsHint>
              {t(
                config.status === 'error'
                  ? 'settingsWindow.configLoadFailed'
                  : 'settingsWindow.unavailable',
              )}
            </SettingsHint>
          )}
        </div>
      )}
    </SettingsPane>
  );
}
